import { useEffect, useRef, useState } from "react";
import type { BarChatConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { tx } from "../lib/tx";

/**
 * "What's the Wi-Fi?" bar counter conversation. Sequential speaker cards
 * (bartender, then a stranger on the next stool) mirroring TechRequest's
 * one-line-at-a-time reveal, but with MULTIPLE named speakers each getting
 * their own portrait + voice — a lightweight chat-thread visualization of a
 * face-to-face exchange instead of a single off-screen narrator. The
 * learner's own lines get their own portrait-less "You" bubble PLUS a
 * spoken voice (item b) so the exchange reads as a real back-and-forth
 * rather than a silent narrator reading everyone else's lines.
 */
export function BarChat({
  config,
  onDone,
  onCameraCue,
}: {
  config: BarChatConfig;
  onDone: () => void;
  /** Fired the instant a line carrying `cameraWaypointId` becomes the active
   *  speaker, so the parent can rotate the camera to face them as they
   *  start talking (see `cameraWaypointId` on `BarChatConfig`). */
  onCameraCue?: (waypointId: string) => void;
}) {
  const [shown, setShown] = useState(0);
  const [speakingIdx, setSpeakingIdx] = useState<number | null>(null);
  const ran = useRef(false);
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    let cancelled = false;
    (async () => {
      void Promise.all(
        config.lines.map((l) =>
          audio.prefetchSpeech(l.line, {
            voice: l.voiceId ?? (l.speaker === "learner" ? "verse" : "onyx"),
            lang: "en-US",
            instructions: l.voiceInstructions,
          }),
        ),
      );
      for (let i = 0; i < config.lines.length; i++) {
        if (cancelled) return;
        const l = config.lines[i];
        if (l.cameraWaypointId) onCameraCue?.(l.cameraWaypointId);
        setSpeakingIdx(i);
        await audio.speak(l.line, {
          voice: l.voiceId ?? (l.speaker === "learner" ? "verse" : "onyx"),
          lang: "en-US",
          instructions: l.voiceInstructions,
          onStart: () => {
            if (!cancelled) setShown(i + 1);
          },
        });
        if (cancelled) return;
        setSpeakingIdx(null);
        await new Promise((r) => window.setTimeout(r, 350));
      }
    })();
    return () => {
      cancelled = true;
      audio.cancelSpeech();
    };
  }, [config]);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: "smooth" });
  }, [shown]);

  const ready = shown >= config.lines.length;
  const aside = config.lines
    .slice(0, Math.max(shown, speakingIdx != null ? speakingIdx + 1 : 0))
    .some((l) => l.speaker === "stranger");

  return (
    <div className={`barchat${aside ? " barchat--aside" : ""}`}>
      <div className="bc-panel">
        <div className="bc-head">
          <span className="bc-head-dot" aria-hidden />
          {tx("Bar counter")}
        </div>
        <div className="bc-thread" ref={threadRef}>
          {config.lines.slice(0, shown).map((l, i) => {
            const initials = l.name
              .split(/\s+/)
              .map((p) => p[0])
              .join("")
              .slice(0, 2)
              .toUpperCase();
            return (
              <div key={i} className={`bc-line bc-line--${l.speaker}`}>
                <span className="bc-avatar" aria-hidden>
                  {l.portraitUrl ? (
                    <img src={l.portraitUrl} alt="" />
                  ) : (
                    <span className="bc-avatar-fallback">{initials}</span>
                  )}
                  <span className={`bc-speak ${speakingIdx === i ? "on" : ""}`} aria-hidden>
                    <i />
                    <i />
                    <i />
                  </span>
                </span>
                <span className="bc-bubble-wrap">
                  <span className="bc-name">
                    {l.name}
                    {l.speakerHint && <span className="bc-name-hint"> — {l.speakerHint}</span>}
                  </span>
                  <span className="bc-bubble">{l.line}</span>
                </span>
              </div>
            );
          })}
        </div>
        <button type="button" className="btn bc-continue" disabled={!ready} onClick={onDone}>
          {ready ? config.continueLabel ?? tx("Continue") : "\u2026"}
        </button>
      </div>
    </div>
  );
}
